import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Heading, Modal, TextButton } from '@zillow/constellation-10';
import * as rentReportingConstants from '../../../../constants/rentReportingConstants';
import { useEnrollRenterInCreditTracking } from '../../../../hooks/usePaymentReporting';
import { updateArrayUserToken } from '../../../../actions/onboarding';
import { showPageNotification } from '@common/actions/NotificationActions';
import { CREDIT_TRACKING_ENROLL_FAILURE, CREDIT_TRACKING_ENROLL_SUCCESS } from '@renter-hub/constants/notifications';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
type ArrayEventMetadata = Record<string, string>;

interface ArrayEventScreenSize {
  width: number;
  height: number;
}

interface ArrayEventDeviceInfo {
  deviceType: string;
  browser: string;
  os: string;
  screenSize: ArrayEventScreenSize;
}

type ArrayEventFlags = Record<string, boolean>;

interface ArrayEvent {
  metadata: ArrayEventMetadata;
  sandbox: boolean;
  component: string;
  componentName: string;
  appKey: string;
  userId: string | null;
  tagName: string;
  event: string;
  version: string;
  deviceInfo: ArrayEventDeviceInfo;
  anonymousId: string;
  flags: ArrayEventFlags;
  userToken: string | null;
}

interface ArrayAccountEnrollProps {
  firstName: string;
  lastName: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
}

const ArrayAccountEnroll: React.FC<ArrayAccountEnrollProps> = ({
  firstName,
  lastName,
  streetAddress,
  city,
  state,
  zipCode,
  email,
}) => {
  const dispatch = useDispatch();
  const [arrayUserId, setArrayUserId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const arrayUserIdRef = useRef<string | null>(null);

  // React Query hook for enrolling in credit tracking
  const enrollMutation = useEnrollRenterInCreditTracking();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('autoOpenEnroll') === 'true') {
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    arrayUserIdRef.current = arrayUserId;
  }, [arrayUserId]);

  const enrollRenterInCreditTrackingHandler = async (arrayUserIdParam: string, arrayUserToken: string) => {
    try {
      await enrollMutation.mutateAsync({ postData: { arrayUserId: arrayUserIdParam } });
      dispatch(updateArrayUserToken(arrayUserToken));
      dispatch(showPageNotification(CREDIT_TRACKING_ENROLL_SUCCESS));
    } catch {
      dispatch(showPageNotification(CREDIT_TRACKING_ENROLL_FAILURE));
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleArrayEvent = async (event: CustomEvent<ArrayEvent>) => {
      if (event.detail.tagName === 'array-authentication-kba') {
        if (event.detail.event === 'set-user-id') {
          const userId = event.detail.userId;
          if (userId) {
            setArrayUserId(userId);
          }
        }
        if (event.detail.event === 'success') {
          const arrayUserToken = event.detail.metadata['user-token'];
          if (arrayUserIdRef.current !== null) {
            await enrollRenterInCreditTrackingHandler(arrayUserIdRef.current, arrayUserToken);
            setIsOpen(false);
          }
        }
      }
    };

    window.addEventListener('array-event', handleArrayEvent as unknown as EventListener);

    return () => {
      window.removeEventListener('array-event', handleArrayEvent as unknown as EventListener);
    };
  }, []);

  const openModalHandler = (open: boolean) => {
    setIsOpen(open);
    RachelCSEvents.emitGetStartedTrackingEvent();
  };

  return (
    <Modal
      trigger={
        <Box css={{ '@media (min-width: 768px)': { textAlign: 'center' } }}>
          <TextButton textStyle="body-bold">Get started</TextButton>
        </Box>
      }
      header={<Heading level={2}>Track your credit</Heading>}
      footer={<></>}
      size="md"
      open={isOpen}
      onOpenChange={openModalHandler}
      body={
        <div
          style={{ minHeight: '800px', padding: '16px' }}
          dangerouslySetInnerHTML={{
            __html: `
                <array-account-enroll
                    appKey="${rentReportingConstants.CREDIT_TRACKING_CONFIG.appKey}"
                    ${!rentReportingConstants.isProd ? 'sandbox="true"' : ''}
                    firstName="${firstName}"
                    lastName="${lastName}"
                    streetAddress="${streetAddress}"
                    city="${city}"
                    state="${state}"
                    zipCode="${zipCode}"
                    email="${email}"
                    uponSuccessShow="successMessage"
                    termsOfUseHref="${rentReportingConstants.CREDIT_REPORTING_TERMS_AND_CONDITIONS_URL}"
                    privacyPolicyHref="${rentReportingConstants.CREDIT_REPORTING_PRIVACY_POLICY_URL}"
                    consentRequestorName="Zillow"
                    consentRequestorWebsite="https://www.zillow.com">
                </array-account-enroll>`,
          }}
        />
      }
    />
  );
};

export default ArrayAccountEnroll;
